import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import { toastService } from "@/services/toastService";
import type {
  TelemetryNote,
  CreateTelemetryNoteRequest,
  UpdateTelemetryNoteRequest,
} from "@/types";
import { createCrudHooks } from "@/hooks/crudHooksFactory";
import i18n from "@/i18n/config";

interface ApiError {
  response?: { data?: { error?: { message?: string } } };
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  return (error as ApiError).response?.data?.error?.message ?? fallback;
}

// Hook for fetching telemetry notes by session
const useTelemetryNotesBySession = (
  sessionId: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["telemetryNotes", "session", sessionId],
    queryFn: () => apiClient.getTelemetryNotesBySession(sessionId),
    enabled: !!sessionId && enabled,
    staleTime: 30000, // 30 seconds
  });
};

// Telemetry note CRUD hooks (toast via toastService, custom error extraction)
const telemetryNoteCrudHooks = createCrudHooks({
  create: {
    mutationFn: (noteData: CreateTelemetryNoteRequest) =>
      apiClient.createTelemetryNote(noteData),
    cacheActions: [
      {
        type: "invalidate",
        key: (_, variables) => [
          "telemetryNotes",
          "session",
          variables.sessionId,
        ],
      },
      { type: "invalidate", key: ["telemetryNotes", "filtered"] },
    ],
    onSuccess: () => {
      toastService.success(
        i18n.t("pages:sessions.telemetry.notes.addedToast"),
      );
    },
    onError: (error) => {
      toastService.error(
        getApiErrorMessage(
          error,
          i18n.t("pages:sessions.telemetry.notes.addedFailedToast"),
        ),
      );
    },
  },
  update: {
    mutationFn: ({
      id,
      noteData,
    }: {
      id: string;
      noteData: UpdateTelemetryNoteRequest;
    }) => apiClient.updateTelemetryNote(id, noteData),
    cacheActions: [
      {
        type: "invalidate",
        key: (data) => ["telemetryNotes", "session", data.sessionId],
      },
      { type: "invalidate", key: ["telemetryNotes", "filtered"] },
      { type: "invalidate", key: ["telemetryNotes", "user"] },
    ],
    onSuccess: () => {
      toastService.success(
        i18n.t("pages:sessions.telemetry.notes.updatedToast"),
      );
    },
    onError: (error) => {
      toastService.error(
        getApiErrorMessage(
          error,
          i18n.t("pages:sessions.telemetry.notes.updatedFailedToast"),
        ),
      );
    },
  },
  delete: {
    mutationFn: (id: string) => apiClient.deleteTelemetryNote(id),
    // Invalidate all telemetry notes queries since we don't know which session this belonged to
    cacheActions: [{ type: "invalidate", key: ["telemetryNotes"] }],
    onSuccess: () => {
      toastService.success(
        i18n.t("pages:sessions.telemetry.notes.deletedToast"),
      );
    },
    onError: (error) => {
      toastService.error(
        getApiErrorMessage(
          error,
          i18n.t("pages:sessions.telemetry.notes.deletedFailedToast"),
        ),
      );
    },
  },
});

// Hook for telemetry notes mutations
const useTelemetryNotesMutations = () => {
  const createNoteMutation = telemetryNoteCrudHooks.useCreate();
  const updateNoteMutation = telemetryNoteCrudHooks.useUpdate();
  const deleteNoteMutation = telemetryNoteCrudHooks.useDelete();

  return {
    createNote: createNoteMutation.mutate,
    updateNote: updateNoteMutation.mutate,
    deleteNote: deleteNoteMutation.mutate,
    isCreating: createNoteMutation.isPending,
    isUpdating: updateNoteMutation.isPending,
    isDeleting: deleteNoteMutation.isPending,
  };
};

// Hook for managing telemetry notes state and interactions
export const useTelemetryNotesManager = (sessionId: string) => {
  const [selectedNote, setSelectedNote] = useState<TelemetryNote | null>(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [pendingNoteTimeOffset, setPendingNoteTimeOffset] = useState<
    number | null
  >(null);

  const {
    data: notes = [],
    isLoading,
    error,
  } = useTelemetryNotesBySession(sessionId);
  const {
    createNote,
    updateNote,
    deleteNote,
    isCreating,
    isUpdating,
    isDeleting,
  } = useTelemetryNotesMutations();

  const startCreatingNote = useCallback((timeOffset: number) => {
    setPendingNoteTimeOffset(timeOffset);
    setIsCreatingNote(true);
    setSelectedNote(null);
  }, []);

  const cancelCreatingNote = useCallback(() => {
    setIsCreatingNote(false);
    setPendingNoteTimeOffset(null);
  }, []);

  const handleCreateNote = useCallback(
    (content: string, onSuccessCallback?: () => void) => {
      if (pendingNoteTimeOffset === null) return;

      createNote(
        {
          sessionId,
          timeOffset: pendingNoteTimeOffset,
          content,
        },
        {
          onSuccess: () => {
            setIsCreatingNote(false);
            setPendingNoteTimeOffset(null);
            onSuccessCallback?.();
          },
        },
      );
    },
    [sessionId, pendingNoteTimeOffset, createNote],
  );

  const handleUpdateNote = useCallback(
    (id: string, noteData: UpdateTelemetryNoteRequest) => {
      updateNote({ id, noteData });
    },
    [updateNote],
  );

  const handleDeleteNote = useCallback(
    (id: string) => {
      deleteNote(id);
      setSelectedNote(null);
    },
    [deleteNote],
  );

  const selectNote = useCallback((note: TelemetryNote | null) => {
    setSelectedNote(note);
    setIsCreatingNote(false);
    setPendingNoteTimeOffset(null);
  }, []);

  const getNotesAtTime = useCallback(
    (timeOffset: number, tolerance = 1.0) => {
      return notes.filter(
        (note) => Math.abs(note.timeOffset - timeOffset) <= tolerance,
      );
    },
    [notes],
  );

  return {
    notes,
    selectedNote,
    isCreatingNote,
    pendingNoteTimeOffset,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    startCreatingNote,
    cancelCreatingNote,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    selectNote,
    getNotesAtTime,
  };
};

// Utility function to format time offset for display
export const formatTimeOffset = (timeOffset: number): string => {
  const minutes = Math.floor(timeOffset / 60);
  const seconds = (timeOffset % 60).toFixed(2);
  return `${minutes}:${seconds.padStart(5, "0")}`;
};

// Utility function to get relative time description
export const getRelativeTimeDescription = (
  timeOffset: number,
  sessionDuration?: number,
): string => {
  if (!sessionDuration || sessionDuration <= 0) {
    // Fallback to original behavior if session duration is not available
    if (timeOffset < 10) return "segmentStart";
    if (timeOffset < 60) return "segmentEarly";
    if (timeOffset < 300) return "segmentMid";
    return "segmentLate";
  }

  const relativePosition = timeOffset / sessionDuration;

  if (relativePosition < 0.1) return "segmentStart"; // First 10%
  if (relativePosition < 0.33) return "segmentEarly"; // First third
  if (relativePosition < 0.67) return "segmentMid"; // Middle third
  return "segmentLate"; // Final third
};
